import { Input } from '@components';
import { yupResolver } from '@hookform/resolvers/yup';
import { useScreenSize } from '@hooks';
import { userApi, utilSlice } from '@store';
import { Button, Flex, theme, Typography } from '@styles';
import { schemas } from '@utils';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { getErrorNotificationText } from 'src/helpers/error';

const SettingsForm = () => {
  const { i18n } = useTranslation();
  const { isTabletSM } = useScreenSize();
  const dispatch = useDispatch();

  const { data } = userApi.useGetUserQuery();
  const [changePassword, { isLoading: isChangePasswordMutationLoading }] =
    userApi.useChangePasswordMutation();
  const { clearErrors, control, formState, handleSubmit, reset, setError } =
    useForm({
      defaultValues: {
        newPassword: '',
        oldPassword: '',
        repeatNewPassword: '',
        username: data?.data.username || '',
      },
      mode: 'onChange',
      reValidateMode: 'onChange',
      resolver: yupResolver(schemas.changePasswordForm),
    });

  const handleChangePasswordButtonClick = () => {
    handleSubmit(async (formData) => {
      try {
        clearErrors('server');
        await changePassword({
          newPassword: formData.newPassword,
          oldPassword: formData.oldPassword,
        }).unwrap();

        dispatch(
          utilSlice.actions.setToastConfig({
            disappearTimeoutInSeconds: 3,
            text: i18n.t('ProfilePage.ChangePassword.Toast.Success'),
            variant: 'success',
          })
        );

        reset();
      } catch ({ data: errorData }) {
        setError('server', {
          message: errorData?.error?.message,
          type: 'server',
        });
      }
    })();
  };

  const errorNotificationText = getErrorNotificationText(formState.errors);
  const isErrorNotificationVisible = !!errorNotificationText;

  return (
    <FormProvider control={control} formState={formState}>
      <Flex flexDirection="row">
        <Flex
          flex={1}
          flexDirection="column"
          justifyContent="center"
          zIndex={3}
        >
          <>
            <Flex
              borderBottomColor={theme.colors.tundora}
              borderBottomStyle="solid"
              borderBottomWidth={2}
              maxWidth={isTabletSM ? 'calc(100% - 38px)' : 396}
              mb={48}
            >
              <Flex flexDirection="column" gap={24} pb={24} width="100%">
                <Typography
                  color={theme.colors.white[100]}
                  variant="MS-regular-s26-lh32"
                  whiteSpace="nowrap"
                >
                  {i18n.t('ProfilePage.PersonalInformation')}
                </Typography>
                <Input
                  backdropFilter="blur(17px)"
                  disabled
                  mb={3}
                  name="username"
                  type="text"
                  variant="username"
                />
              </Flex>
            </Flex>
            <Flex
              borderBottomColor={theme.colors.tundora}
              borderBottomStyle="solid"
              borderBottomWidth={2}
              maxWidth={isTabletSM ? 'calc(100% - 38px)' : 396}
              mb={48}
            >
              <Flex flexDirection="column" gap={24} pb={24} width="100%">
                <Typography
                  color={theme.colors.white[100]}
                  variant="MS-regular-s26-lh32"
                  whiteSpace="nowrap"
                >
                  {i18n.t('ProfilePage.ChangePassword.Title')}
                </Typography>
                <Input
                  backdropFilter="blur(17px)"
                  mb={3}
                  name="oldPassword"
                  type="password"
                  variant="oldPassword"
                />
                <Input
                  backdropFilter="blur(17px)"
                  mb={3}
                  name="newPassword"
                  type="password"
                  variant="newPassword"
                />
                <Input
                  backdropFilter="blur(17px)"
                  mb={isErrorNotificationVisible ? 1 : 3}
                  name="repeatNewPassword"
                  type="password"
                  variant="repeatNewPassword"
                />
                {isErrorNotificationVisible && (
                  <Typography
                    color={theme.colors.red[100]}
                    maxWidth={isTabletSM ? 'calc(100% - 38px)' : 396}
                    mb={3}
                    variant="MS-regular-s16-lh25"
                  >
                    {!formState.errors.server
                      ? i18n.t(errorNotificationText)
                      : errorNotificationText}
                  </Typography>
                )}
              </Flex>
            </Flex>
          </>
          <Button
            disabled={!formState.isValid || isChangePasswordMutationLoading}
            maxWidth={isTabletSM ? 'calc(100% - 38px)' : 396}
            onClick={handleChangePasswordButtonClick}
            type="submit"
            variant="primary"
          >
            <Typography
              color={
                !formState.isValid || isChangePasswordMutationLoading
                  ? '#6d6d6d'
                  : theme.colors.black[100]
              }
              variant="MS-bold-s16-lh25"
            >
              {i18n.t('ProfilePage.Save').toUpperCase()}
            </Typography>
          </Button>
        </Flex>
      </Flex>
    </FormProvider>
  );
};
export default SettingsForm;
